/**
 * @file        src/lib/useCompany.js
 * @description The ISSUING COMPANY printed on admin documents (tax invoice,
 *              e-ticket, hotel voucher), resolved live from the tenant's own
 *              Site Settings row so each tenant bills under its own entity.
 *
 *              Previously this hook overlaid ONLY `address` and let every other
 *              field — legal name, email, phone, support, website, jurisdiction
 *              — fall through to hardcoded "Neon Holidays" constants. Tenant 2
 *              (Beyond) therefore printed tenant 1's company on its invoices and
 *              e-tickets: the right address under the wrong legal entity, which
 *              is worse than an obviously blank field.
 *
 *              Resolution order per field: site_settings (tenant DB) → the
 *              tenant's registry name where it is a truthful stand-in for the
 *              display name → '—'. It NEVER falls back to another tenant's
 *              details; an unconfigured tenant shows blanks, which is honest and
 *              visibly prompts an admin to fill in Site Setting → Company.
 * @author      Kinetika Labs
 * @created     2026-07-23
 * @changelog
 *   - 2026-07-23 Claude — Initial: single source of truth for the company address.
 *   - 2026-08-10 Claude — Resolve the WHOLE issuer from site_settings (migration
 *                          051), not just the address; tenant-name fallback for
 *                          the display name; no Neon literals anywhere.
 *   - 2026-09-10 Claude — Also exposes `state` — our registered state, already in
 *                          the /site-settings/company projection but never read
 *                          here. The hotel tax invoice needs it as the PLACE OF
 *                          SUPPLY comparison (hotel's state vs ours → CGST+SGST or
 *                          IGST). Unlike every other field it is '' when
 *                          unconfigured, NOT the '—' placeholder: it is a decision
 *                          input, not printed text, and a dash compares unequal to
 *                          every real state name — which would have flipped an
 *                          in-state stay to IGST on any tenant that has not filled
 *                          its address in.
 *   - 2026-09-15 Claude — Exposes the STATUTORY identity: gstin, pan,
 *     registrationNo, hsn, gstStateCode and the configurable invoice `terms`
 *     (migration 056). The admin's copy of a tax invoice printed no supplier
 *     GSTIN at all — this hook had no field for one — while the customer's copy
 *     printed a build-time placeholder. Both are now the tenant's own value.
 */
import { useQuery } from '@tanstack/react-query';
import { get } from '../api/client';
import { qk } from '../api/queryKeys';
import { COMPANY as DEFAULTS, UNSET } from './company';
import { useBranding } from './useBranding';

// Compose the one-line postal address from the Site Settings row.
export function composeAddress(s) {
  if (!s) return '';
  return [s.address, s.city, s.state, s.country, s.pincode]
    .map((x) => String(x || '').trim())
    .filter(Boolean)
    .join(', ');
}

const clean = (v) => String(v ?? '').trim();
/** First non-empty candidate, else the neutral placeholder. */
const pick = (...vals) => vals.map(clean).find(Boolean) || UNSET;

/**
 * @returns {{name,legal,address,state,email,phone,support,web,jurisdiction}} the
 * tenant's issuing company. Every field is a string; unconfigured fields are '—'.
 */
export function useCompany() {
  const { data } = useQuery({
    // NOT /site-settings: that route is gated on the 'Site Management' module,
    // which a booking-desk staff account who prints invoices does not have.
    queryKey: qk.siteSettings.company,
    queryFn: () => get('/site-settings/company'),
    staleTime: 5 * 60 * 1000,
  });
  // Tenant registry name — a truthful display-name fallback for THIS tenant
  // (it is what the tenant is called), unlike the old hardcoded constant.
  const { name: tenantName } = useBranding();

  const s = data?.data;
  const name = pick(s?.company_name, tenantName);
  return {
    ...DEFAULTS,
    name,
    // Falls back to this tenant's own display name rather than leaving the
    // invoice's From block blank — still never another tenant's entity.
    legal: pick(s?.company_legal_name, name),
    address: pick(composeAddress(s)),
    // '' (not UNSET) when unconfigured — see the file header: this one is compared,
    // not printed, and an unknown state must read as unknown.
    state: clean(s?.state),
    email: pick(s?.email),
    phone: pick(s?.phone),
    support: pick(s?.support_email, s?.email),
    web: pick(s?.website),
    jurisdiction: pick(s?.jurisdiction, s?.city),
    // Statutory identity. Printed, so these take the '—' placeholder treatment.
    gstin: pick(s?.gst_no),
    pan: pick(s?.pan),
    registrationNo: pick(s?.registration_no),
    hsn: pick(s?.hsn_code),
    // NOT placeholder-filled, for the same reason `state` above is not: this is
    // a value the document COMPARES or omits, never one it prints as a dash.
    gstStateCode: clean(s?.gst_state_code),
    terms: clean(s?.terms_conditions),
  };
}
